// Les 10 du jour partiels de l'invité (spec § 3, aperçu de la base) : dans la zone d'intérêt qu'il choisit, les fiches
// qui bougent et qu'il ne voit pas, sans nom ni lien (catégorie, pays, phase, tranche de montant, score, genre du
// signal). Calculées chaque nuit par `radar priorities`, lues par `daily_teasers_today`.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";
import { PhaseBadge } from "./PhaseBadge";
import { categoryKey, countryName } from "./projectUtils";
import { LockNote } from "./Teaser";
import { useCoverage, useZones } from "./territories";

type Teaser = {
  rank: number;
  category: string | null;
  country: string | null;
  phase: string | null;
  amount_band: string | null;
  score: number | null;
  reasons: string[];
  day: string;
};

export const QUERY_KEY = ["daily_teasers_today"];

export const DailyTeasers = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.teaser.${key}`, options);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const { data: sale, refetch } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: identity?.id != null },
  );
  const { data: zones = [] } = useZones();
  const { data: coverage = [] } = useCoverage();
  const { data: teasers = [] } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<Teaser[]> => {
      const { data, error } = await getSupabaseClient().rpc(
        "daily_teasers_today",
      );
      if (error) throw error;
      return (data ?? []) as Teaser[];
    },
  });

  const area = sale?.interest_zone
    ? `zone:${sale.interest_zone}`
    : sale?.interest_country
      ? `country:${sale.interest_country}`
      : "";
  const choose = async (value: string) => {
    const [kind, code] = value.split(":");
    const { error } = await getSupabaseClient().rpc("set_interest_area", {
      zone: kind === "zone" ? code : null,
      country: kind === "country" ? code : null,
    });
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    await refetch();
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    notify(t("interest_saved"));
  };
  const countries = [...coverage].sort((a, b) =>
    countryName(a.country, locale).localeCompare(
      countryName(b.country, locale),
      locale,
    ),
  );

  return (
    <div className="flex flex-col gap-3 border-t border-border px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">{t("interest_label")}</span>
        <Select value={area} onValueChange={choose}>
          <SelectTrigger size="sm" className="w-56">
            <SelectValue placeholder={t("interest_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{t("interest_zones")}</SelectLabel>
              {zones.map((zone) => (
                <SelectItem key={zone.code} value={`zone:${zone.code}`}>
                  {translate(`resources.projects.zones.${zone.code}`)}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>{t("interest_countries")}</SelectLabel>
              {countries.map((row) => (
                <SelectItem key={row.country} value={`country:${row.country}`}>
                  {countryName(row.country, locale)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      {!area ? (
        <p className="text-sm text-muted-foreground">{t("interest_hint")}</p>
      ) : !teasers.length ? (
        <p className="text-sm text-muted-foreground">{t("teasers_none")}</p>
      ) : (
        <>
          <ol
            className="flex flex-col divide-y"
            aria-label={t("teasers_title")}
          >
            {teasers.map((teaser) => (
              <li
                key={teaser.rank}
                className="flex items-start gap-3 py-2 text-muted-foreground"
              >
                <span className="w-8 shrink-0 text-lg font-bold leading-6 tabular-nums text-foreground">
                  {teaser.score ?? "–"}
                </span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <PhaseBadge phase={teaser.phase} />
                  {teaser.category && (
                    <span>{translate(categoryKey(teaser.category))}</span>
                  )}
                  <span>{countryName(teaser.country, locale)}</span>
                  {teaser.amount_band && (
                    <span>{t(`bands.${teaser.amount_band}`)}</span>
                  )}
                  {teaser.reasons.map((kind) => (
                    <span
                      key={kind}
                      className="rounded-md border border-border bg-muted/40 px-2 py-0.5"
                    >
                      {t(`signals.${kind}`)}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ol>
          <LockNote>
            {t("teasers_lock", { smart_count: teasers.length })}
          </LockNote>
        </>
      )}
    </div>
  );
};
